import { Make } from '../models/brand.model';
import { IEmployee, IServiceOpcode, IVehicle, PricingType, Brand, VehicleType, VehiclesOfInterest } from '@signal/asp-data-commons';
import { VehicleModelYearList } from '../models/series-details';
import { environmentConst } from '../../../../environments/environment';
import { TranslateService } from  '@ngx-translate/core';
import { DateTime } from 'luxon';
import { UserProfileModel } from '../models/user-profile.model';
import { ErrorStateMatcher } from '@angular/material/core';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';

export const imageBaseURL = 'https://www.toyota.com/';
export let currentCurrency:string = 'USD';
export let currentLanguageLocale:string = 'en-US';
export let countryCd:string = 'USA';
export let globalTXM:boolean =  false;
export let isAspCrmIntegrationEnabled:boolean = false;
export let isAspDmsIntegrationEnabled:boolean = false;
export const cacheLimitedStartDate:DateTime = DateTime.fromISO(new Date().toISOString()).plus({days:-40});
export let userProfile: UserProfileModel;
export let isSpServiceDashboardOverride = false;
export let isSpServiceSchedulingOverride = false;
export let isSalesAccessibleFeatureFlag = false;
export let isServiceAccessibleFeatureFlag = false;
export let isPartsAccessible = false;
export let isDeliveryChecklistAccessible = false;
let isLicenseToVinEnabled = false;

export const assetUrl = (url: string): string => {
  // @ts-ignore
  const publicPath = environment.spaHost;
 
  const publicPathSuffix = publicPath.endsWith('/') ? '' : '/';
  const urlPrefix = url.startsWith('/') ? '' : '/'
 
  return `${publicPath}${publicPathSuffix}assets${urlPrefix}${url}`;
}
export const setDefaultImg = (event , val)=>{
  event.target.onerror = null;
  event.target.src = assetUrl(val);
}

/**
 * Returns the the range including start and end with stepper as 1
 *
 * @param start number for producing the range
 * @param end number for producing the range
 * @returns number[]
 */
export const range = (start: number, end: number): number[] => {
  return Array(end - start + 1)
    .fill(0)
    .map((_, idx) => start + idx);
};

/**
 * Returns brand object array
 */
export const getMakes = (): Make[] => {
  const brand = environment.brand.toLocaleLowerCase();
  const toyota: Make = { name: 'Toyota', code1: 'T', code3: 'TOY' };
  const lexus: Make = { name: 'Lexus', code1: 'L', code3: 'LEX' };
  return [
    brand === Brand.TOYOTA.toLocaleLowerCase() ? toyota : lexus,
    brand === Brand.TOYOTA.toLocaleLowerCase() ? lexus : toyota,
    { name: 'Other', code1: 'O', code3: 'OTH' }
  ];
};

/**
 * Extracts the object keys as number array
 *
 * @param object the input object
 * @param addOnlyNumber flag to exclude if a key is not a proper number
 *        Default is true
 * @param roundOff flag to add only integers
 *        Default is true
 */
export const getIntegerKeys = (object: any, addOnlyNumber: boolean = true, roundOff: boolean = true): any[] => {
  const keys = [];
  Object.keys(object).forEach(key => {
    const parsedNumber = roundOff ? parseInt(key, 10) : parseFloat(key);
    if (!isNaN(parsedNumber) && addOnlyNumber) {
      keys.push(parsedNumber);
    } else if (!addOnlyNumber) {
      keys.push(key);
    }
  });
  return keys;
};

/**
 * Extracts the years list from the given model year and series object
 *
 * @param modelYearSeries input model year and series
 */
export const getModelYears = (modelYearSeries: VehicleModelYearList): number[] => {
  const years = getIntegerKeys(modelYearSeries);
  return sort(years, 'DESC');
};

/**
 * Sorting an array
 *
 * @param array input array
 * @param order order of sorting.
 *        - ASC - Ascending
 *        - DESC - Descending
 *        - Default is ASC
 */
export const sort = (array: any[], order: 'ASC' | 'DESC' = 'ASC') => {
  return array.sort((a, b) => {
    if (order === 'ASC') {
      return a - b;
    } else {
      return b - a;
    }
  });
};

export const inDealerSite = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

export const groupBy = (items: any[], key: string): { [key: string]: any[] } => {
  return items.reduce(
    (result, item) => ({
      ...result,
      [item[key]]: [
        ...(result[item[key]] || []),
        item,
      ],
    }),
    {},
  );
};

export const toFixed = (value: number, precision?: number) => {
  const power = Math.pow(10, precision || 0);
  return (Math.round(value * power) / power);
};

export const toUsDollar = ((input: number) =>{
  if(countryCd==='MEX'){
    return 'MX'+new Intl.NumberFormat(currentLanguageLocale, { style: 'currency', currency: currentCurrency }).format(input);
  }
 return new Intl.NumberFormat(currentLanguageLocale, { style: 'currency', currency: currentCurrency }).format(input);
})
export const getVinDisplayText = (vehicle: IVehicle) => {
  return `VIN ${vehicle.vin || 'service.notAvailable'}`;
};

export const getCarImageAbsoluteURL = (imageUrl: string) => {
  let url = imageBaseURL + (imageUrl || 'toyota-owners-theme/_assets/vehicles/Sedan_Generic/Sedan_Generic_base_default_34_f_d_490x280.png');
     if(imageUrl && imageUrl.startsWith('/',0)){
      url = imageBaseURL + `${imageUrl.substring(1)}`;
     } else if(imageUrl && imageUrl.includes('https')){
      url = imageUrl;
     }
  return url;
};

export const titleCase=(str)=>{
  return str?.toString().charAt(0)?.toUpperCase()+str?.toString()?.substr(1)?.toLowerCase() ;
}

const titleCaseWords = (value: string): string => {
  return value
    ?.toString()
    .split(/\s+/)
    .filter(word => !!word)
    .map(word => titleCase(word))
    .join(' ')
    .trim();
}

export interface CustomerDisplayNameOptions {
  fallback?: string;
  businessLabel?: string;
}

export const getCustomerDisplayName = (customer: any): string => {
  const fallback = 'NA';

  if (!customer) {
    return fallback;
  }

  const customerType = customer.customerType?.toString().toLowerCase();
  const businessName = titleCaseWords(customer?.businessName || '');
  if (customerType === 'business' && businessName) {
    return `Business: ${businessName}`;
  }

  const nameParts = [customer.firstName, customer.lastName]
    .map(part => titleCaseWords(part || ''))
    .filter(part => !!part);
  const displayName = nameParts.join(' ');

  return displayName.trim() || fallback;
};

export const getMakeModelYearText = (vehicle: IVehicle) => {
  //let index=vehicle?.description.indexOf(" ");
  return vehicle?.model ? `${titleCase(vehicle?.make)} ${(vehicle?.model)} ${vehicle?.year}` : '';
  //[titleCase(vehicle?.make), vehicle?.description?.substr(index), vehicle?.year].filter(i => !!i).join(' ');
};

export const getTradeInVehicleSummaryText = (tradeInVehicles: Partial<IVehicle>[] = []): string => {
  return (tradeInVehicles || [])
    .map(vehicle => [vehicle?.year, vehicle?.make, vehicle?.model].filter(value => !!value).join(' ').trim())
    .filter(value => !!value)
    .join('\n');
};

export const unsubscribeSubscriptions = (subscriptions: any) => {
   if (!subscriptions) {
    return;
   }
   if (typeof subscriptions.unsubscribe === 'function') {
    subscriptions.unsubscribe();
    return;
   }
   if (Array.isArray(subscriptions)) {
    subscriptions.forEach(sub => {
     if (sub && typeof sub.unsubscribe === 'function') {
      sub.unsubscribe();
     }
    });
    return;
   }
   for (const key of Object.keys(subscriptions)) {
    const sub = subscriptions[key];
    if (sub && typeof sub.unsubscribe === 'function') {
     sub.unsubscribe();
    }
   }
};

export const getEmployeeNameInitials = (employee: IEmployee) => {
  if (!(employee && employee.firstName)) {
    return '';
  }

  let initial = employee.firstName.charAt(0).toUpperCase();
  if (employee.lastName) {
    initial += employee.lastName.charAt(0).toUpperCase();
  } else {
    initial += employee.firstName.charAt(1).toLowerCase();
  }
  return initial;
};

export const deepCopy = (object: any) => JSON.parse(JSON.stringify(object));

export function upsert(array, item, predicate = _item => _item.id === item.id) { // (1)
  const i = array.findIndex(predicate);
  if (i > -1) {
    array[i] = item;
  }// (2)
  else {
    array.push(item);
  }
  return array;
}

export function getOpcodeDisplayPricing(serviceOpcode: IServiceOpcode) {
  return `${getOpcodePricingText(serviceOpcode)} ${getOpcodeFinalPricing(serviceOpcode)}`;
}

export function getOpcodePricing(serviceOpcode: IServiceOpcode) {
  switch (serviceOpcode?.pricing?.pricingType) {
      case PricingType.FIXED:
        const fixedprice = (serviceOpcode.pricing[PricingType.FIXED]) ? (serviceOpcode.pricing[PricingType.FIXED]) : 0;
        return toUsDollar(fixedprice);
      case PricingType.STARTS_AT:
        const startsAt = (serviceOpcode.pricing[PricingType.STARTS_AT]) ? (serviceOpcode.pricing[PricingType.STARTS_AT]) : 0;
        return toUsDollar(startsAt);
      case PricingType.RANGE:
        const fromRange = (serviceOpcode.pricing[PricingType.RANGE].from) ? (serviceOpcode.pricing[PricingType.RANGE].from) : 0;
        const toRange = (serviceOpcode.pricing[PricingType.RANGE].to) ? (serviceOpcode.pricing[PricingType.RANGE].to) : 0;
        return `${toUsDollar(fromRange)} - ${toUsDollar(toRange)}`;
      default:
        return toUsDollar(0);
  }
}

export function getOpcodeFinalPricing(serviceOpcode: IServiceOpcode) {
  return toUsDollar(!isNaN(serviceOpcode?.pricing?.final) ? serviceOpcode?.pricing?.final : 0);
}

export function getOpcodePricingText(serviceOpcode: IServiceOpcode) {
  switch (serviceOpcode?.pricing?.pricingType) {
    case PricingType.FIXED:
      return '';
    case PricingType.STARTS_AT:
      return 'Starts at';
    case PricingType.RANGE:
      return `Ranges from`;
    default:
      return '';
  }
}

export function extractDate(date: (string | Date) = new Date()) {
  return new Date(extractDateAsString(date));
}

export function extractDateAsString(date: (string | Date) = new Date()) {
  return (typeof (date) === 'string') ? date : resetTimeZone(date)
    .toISOString()
    .split('T')[0];
}

/*export function getDate(date: any = new Date()): any {
  return (typeof (date) === 'string') ? date : resetTimeZone(date);
}*/

export function resetTimeZone(date: Date) {
  return new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
}

export const GLOBAL_CONSTANT = {
  appointmentDateFormat: 'YYYY-MM-DDTHH:mm:ss.000',
  apptScheduledInfoFormat: 'MMM DD [at] hh:mm a',
  telematicsDisclaimerText: `otherTexts.telematicsDisclaimerText`,
  schedulingDisclaimerText: `testDrive.privacyPolicyTxt`,
  placeholderCarImage: '/toyota-owners-theme/_assets/vehicles/Sedan_Generic/Sedan_Generic_base_default_34_f_d_490x280.png',
  dcsConfig: {
    timeout: 10000,
    retryCount: 3
  },
  defaultLanguage: 'en',
  errorMessage: 'otherTexts.somethingWentWrongPleaseTryAgainOrContactTheDealership'
};

export function getAlertErrorMessage(error: any, fallback = GLOBAL_CONSTANT.errorMessage) {
  return error?.error?.status?.messages?.[0]
    || error?.error?.message
    || error?.message
    || fallback;
}

export function setCurrentCurrency(currency:string) {
  currentCurrency = currency;
}
export function setCurrentLanguageLocale(languageLocale:string) {
  currentLanguageLocale = languageLocale;
}
export let environment = environmentConst.LOCAL;

export function loadEnvironmentVariables(window: any) {
  if (window.location.hostname.includes('dev.dealer') || window.location.hostname.includes('dealer.dev')) {
    environment = environmentConst.DEV;
  } else if (window.location.hostname.includes('test.dealer') || window.location.hostname.includes('dealer.test')) {
    environment = environmentConst.TEST;
  } else if (window.location.hostname.includes('qa.dealer') || window.location.hostname.includes('dealer.qa')) {
    environment = environmentConst.QA;
  } 
  else if (window.location.hostname.includes('dealer')) {
    environment = environmentConst.PROD;
  }
  else {
    environment = environmentConst.LOCAL;
  }
}

export let dealerCd = '';

export function setBrand(dealerCode: string) {
  dealerCd = dealerCode;
  // If Dealer Code starts with 6, it is a Lexus dealer
  if (dealerCode.startsWith('6')) {
    environment.brand = Brand.LEXUS.toLocaleLowerCase();
    environment.xBrand = 'L';
    environment.xClient = 'LD-WEB';
  }
  // Else Toyota dealer
  else {
    environment.brand = Brand.TOYOTA.toLocaleLowerCase();
    environment.xBrand = 'T';
    environment.xClient = 'TO-WEB';
  }
}

export function setCountryCode(countryCode: string){
  countryCd = countryCode;
}

export function setGlobalTXM(txmVal: boolean){
  globalTXM = txmVal;
}

export function setDmsAndCrmIntegration(dms:boolean , crm:boolean){
  isAspDmsIntegrationEnabled = dms
  isAspCrmIntegrationEnabled = crm
}

export function setOverrideValues(dashboard, scheduling, salesAccessibleFeatureFlag, serviceAccessibleFeatureFlag, partsAccessibleFeatureFlag, deliveryChecklist) {
  isSpServiceDashboardOverride = dashboard;
  isSpServiceSchedulingOverride = scheduling;
  isSalesAccessibleFeatureFlag = salesAccessibleFeatureFlag;
  isServiceAccessibleFeatureFlag = serviceAccessibleFeatureFlag;
  isPartsAccessible = partsAccessibleFeatureFlag;
  isDeliveryChecklistAccessible = deliveryChecklist
}

export function getFeatureFlags() {
  return {
    "isSpServiceDashboardOverride": isSpServiceDashboardOverride,
    "isSalesAccessibleFeatureFlag": isSalesAccessibleFeatureFlag,
    "isServiceAccessibleFeatureFlag": isServiceAccessibleFeatureFlag,
    "isPartsAccessible": isPartsAccessible,
    "isDeliveryChecklistAccessible": isDeliveryChecklistAccessible
  }
}



export const SkipInterceptorHeader = 'x-skip-interceptor';

/*
 * Role Section starts
 */

export const AspRoles = ['DD_ASP'];
export const AspSalesAdminRoles = ['DD_NewVehicleVMSIncludeCost'];
export const AspServiceAdminRoles = ['DD_ServiceManagement'];
export const AspServiceAdminRolesT2 = ['DD_TIS'];
export const DD365Roles = ['DD_Enrolled'];
export const DD365DistRoles = ['DD_Dist_Enrolled'];
export const AspPartsAdminRoles = ['DD_PartsManagement'];

/*
 * Role Section ends
 */
export const scionModelList = ["FR-S","iA","iM","iQ","iQ EV","tC","xA","xB","xD"];

export const newScionModelList = ["Scion fr-s","Scion iq","Scion tc","Scion xa","Scion xb","Scion xd","Scionia","Scionim","iQ EV"];

/*
Routes
*/
export const SalesUrls = [
  '/asp/bdc/pending',
  '/asp/bdc/appointment-conflicts',
  '/asp/bdc/vehicleinquiry',
  '/asp/bdc/testdrive',
  '/asp/bdc/delivery',
  '/asp/bdc/tech-appointment',
  '/asp/bdc/salesscheduledashboard'
];

export const PartsUrls = [
  '/asp/parts',
  '/asp/parts/scheduledashboard',
  '/asp/parts/settings',
  '/asp/parts/manageadvisors',
  '/asp/parts/manageholidays',
  '/asp/bdc/parts',
  '/asp/bdc/partsscheduledashboard'
];

export const ServiceUrls = [
  '/asp/bdc/service',
  '/asp/bdc/scheduledashboard'
];

export const ServiceUrlsExceptions = [
  '/asp/bdc/scheduledashboard'
];

export const ServiceUrlsExceptionsForServiceDashboard = [
  '/asp/bdc/scheduledashboard',
  '/asp/bdc/dashboard',
  '/asp/bdc/dashboard/servicehistory',
  '/asp/bdc/customer/list',
  '/asp/bdc/customer/search',
  '/asp/bdc/customer',
  '/asp/bdc'
];

/*
 * configuration for exit alert
 */
export const exitConfig = {
  width: '450px',
  height: '220px',
  panelClass: 'asp-modal-container',
  data:{
  'popupBodyText':'servicePersonnel.exitConfirmation',
  'popupTitle':'teamManagement.saveConfirmation',
  'saveButton':'overrideCapacity.save',
  'cancelButton':'overrideCapacity.dontSave'
  },
  disableClose:true
};

/*
 * configuration for confirm alert
 */
export const confirmConfig = {
  width: '400px',
  height: '220px',
  panelClass: 'asp-modal-container',
  data:{
  'popupBodyText':'servicePersonnel.closeConfirmation',
  'popupTitle':'teamManagement.closeConfirmation',
  'saveButton':'testDrive.continue',
  'cancelButton':'overrideCapacity.dontSave'
  }
};

/*
 * configuration for checklist confirm alert
 */
export const confirmConfigChecklist = {
  width: '400px',
  height: '220px',
  panelClass: 'asp-modal-container',
  data:{
  'popupBodyText':'deliveryChecklist.exitConfirmation',
  'popupTitle':'teamManagement.closeConfirmation',
  'saveButton':'testDrive.continue',
  'cancelButton':'overrideCapacity.dontSave'
  }
};

/* for checklist duration */
export function convertMinsToHourMins(duration: number, isRoundedValue = false) {
  if(duration > 0) {
    const hours = Math.floor(duration / 60);
    const mins = isRoundedValue ? Math.round(duration % 60) : (duration % 60);
    const hr = hours > 0 ? `${hours}hr` : '';
    const min = mins > 0 ? `${mins}min` : '';
    return `${hr} ${min}`;
  }
  else {
    return '';
  }
}

/* creating this function to use instead of Math.random() to avoid sonarqube security hotspot*/
export function getRandomValue(): number {
   const array = new Uint32Array(1);
   window.crypto.getRandomValues(array);
   return array[0] / 0x100000000;
}

export function setUserProfile(user: UserProfileModel){
  userProfile = user;
}

export function getUserProfile(){
  let obj:any = userProfile
  return obj
}

export function setSessionTable(setting: any, col: any, table: string) {
  const sessionTable = JSON.parse(getSessionTable(table));
  if (!sessionTable) {
    sessionStorage.setItem(table, JSON.stringify([setting]));
  }
  else {
    const check = sessionTable.findIndex(c => c.column === col);
    if (check !== -1) {
      sessionTable[check]['width'] = setting.width
    }
    else {
      sessionTable.push(setting);
    }
    sessionStorage.setItem(table, JSON.stringify(sessionTable));
  }
}

export function getSessionTable(name: string) {
  return sessionStorage.getItem(name);
}

export const getVehicleDisplayTextForEnquiryAppt = (vehicles, translateService: TranslateService) => {
  let vehicleList = '';
  vehicles?.forEach((vehicle, index) => {
    vehicleList += translateService.instant(getVehiclesTranslation(vehicle));
    if (vehicles[index + 1]) {
      vehicleList += ', ';
    }
  });
  return vehicleList;
}

export const getVehicleTypeTraslation = (vehicleType: VehicleType, translateService: TranslateService) => {
  if (!vehicleType) {
    return '';
  }
  return vehicleType === VehicleType.BOTH ? translateService.instant('vehicleInquiry.new') + ', ' + translateService.instant('vehicleInquiry.used') : translateService.instant(`vehicleInquiry.${vehicleType.toLowerCase()}`);
}

export const getVehiclesTranslation = (vehicle: VehiclesOfInterest) => {
  if (vehicle === VehiclesOfInterest.TRUCKS) {
    return 'vehicleInquiry.trucks';
  }
  else if (vehicle === VehiclesOfInterest.CARS_MINIVAN) {
    return 'vehicleInquiry.carsAndMinivan';
  }
  else if (vehicle === VehiclesOfInterest.ELECTRIFIED) {
    return 'vehicleInquiry.electrified';
  }
  else if (vehicle === VehiclesOfInterest.CROSSOVERS_SUVS) {
    return 'vehicleInquiry.crossoversAndSUVs';
  }
}

export function setLicenseToVinEnabled(value: boolean){
  isLicenseToVinEnabled = value;
}

export function getLicenseToVinEnabled() {
  return isLicenseToVinEnabled;
}

export function countryPhoneNumber(countryCd: string) {
  if (!countryCd) {
    return '+1';
  }
  const isUS = countryCd.trim().toUpperCase().includes('US');
  return isUS ? '+1' : '+52';
}

export function countryPhoneCode(countryCd: string) {
  if (!countryCd) {
    return 'US';
  }
  const isUS = countryCd.trim().toUpperCase().includes('US');
  return isUS ? 'US' : 'MX';
}

export class DirtyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    _form: FormGroupDirective | NgForm | null
  ): boolean {
    return !!(control && control.invalid && (control.dirty || control.touched));
  }
}
